



























































































































































































































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch
} from '@vue/composition-api';
import {
  useCreatePaymentPlanSchedule,
  useUpdatePaymentPlanSchedule
} from '@/composable/payment-plan';
import { AxiosError } from 'axios';
import moment from 'moment';

import { cloneDeep } from 'lodash';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';
import {
  IPaymentPlanSchedule,
  IPaymentPlanScheduleData,
  PaymentPlanAmountTypeEnum,
  PaymentPlanScheduleDateTypeEnum,
  PaymentPlanSchedule,
  IPaymentPlan,
  InvoiceStateEnum
} from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { requiredValidator } from '@/utils/validators';
import { ALForm } from '@/common/component.types';

import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import DatePicker from '@/components/common/DatePicker.vue';
import PaymentPlanSchedulePaymentTypePicker from './PaymentPlanSchedulePaymentTypePicker.vue';
import PaymentPlanScheduleDateTypePicker from './PaymentPlanScheduleDateTypePicker.vue';
import InvoicePicker from '@/components/invoice/InvoicePicker.vue';

export default defineComponent({
  name: 'PaymentPlanDialog',
  components: {
    ALDialog,
    ErrorAlert,
    DatePicker,
    PaymentPlanSchedulePaymentTypePicker,
    PaymentPlanScheduleDateTypePicker,
    InvoicePicker
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    paymentPlanSchedule: {
      type: Object as PropType<IPaymentPlanScheduleData | null>,
      default: null
    },
    openDrawerOnCreate: {
      type: Boolean,
      default: false
    },
    paymentPlan: {
      type: Object as PropType<IPaymentPlan>,
      required: true
    },
    totalPlanAmount: {
      type: Number,
      required: true
    }
  },
  emits: ['update:dialog'],
  setup(props, { emit, root }) {
    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const currentPaymentPlan = computed(() => props.paymentPlan!);
    const currentPaymentPlanId = computed(() => props.paymentPlan.id!);
    const currentPaymentSchedule = computed(() => props.paymentPlanSchedule);

    const paymentPlanScheduleData = ref<IPaymentPlanScheduleData>({
      csi_order_number: props.paymentPlan?.csi_order_number ?? '',
      po_number: props.paymentPlan?.po_number ?? '',
      amount_type: PaymentPlanAmountTypeEnum.Percentage,
      invoice: null
    });

    const amountTypeSwitch = ref(
      paymentPlanScheduleData.value.amount_type ===
        PaymentPlanAmountTypeEnum.Percentage
    );

    watch(localDialog, value => {
      if (value) {
        resetFormValidation();
        paymentPlanScheduleData.value = !props.isEdit
          ? ({
              csi_order_number: props.paymentPlan?.csi_order_number ?? '',
              po_number: props.paymentPlan?.po_number ?? '',
              amount_type: PaymentPlanAmountTypeEnum.Percentage,
              invoice: null
            } as IPaymentPlanScheduleData)
          : cloneDeep(props.paymentPlanSchedule!);

        amountTypeSwitch.value =
          paymentPlanScheduleData.value.amount_type ===
          PaymentPlanAmountTypeEnum.Percentage;
      }
    });

    function updateAmountType(value: boolean) {
      if (value) {
        paymentPlanScheduleData.value.amount_type =
          PaymentPlanAmountTypeEnum.Percentage;
        paymentPlanScheduleData.value.fixed_amount = null;
      } else {
        paymentPlanScheduleData.value.amount_type =
          PaymentPlanAmountTypeEnum.FixedAmount;
        paymentPlanScheduleData.value.percentage = null;
      }
    }

    const formRef = ref<ALForm | null>(null);
    const valid = ref(false);

    const subtitle = computed(
      () =>
        `${
          props.isEdit ? 'Update' : 'Create'
        } payment plan schedule information`
    );

    function closeDialog() {
      clearErrors();
      localDialog.value = false;
    }

    const {
      isLoading: isUpdatePending,
      error: updateError,
      mutate: updatePaymentPlan,
      reset: updateReset
    } = useUpdatePaymentPlanSchedule(currentPaymentPlanId);
    const {
      isLoading: isCreatePending,
      error: createError,
      mutate: createPaymentPlan,
      reset: createReset
    } = useCreatePaymentPlanSchedule(currentPaymentPlanId);

    const localError = ref<ISystemError | null>(null);

    const loading = computed(() =>
      props.isEdit ? isUpdatePending.value : isCreatePending.value
    );

    const error = computed<ISystemError | null>({
      get() {
        let systemError: ISystemError | null = localError.value;
        const axiosError = props.isEdit ? updateError.value : createError.value;

        if (axiosError) {
          systemError = setErrorFromAxiosError(axiosError as AxiosError);
        }

        return systemError;
      },
      set(value: ISystemError | null) {
        localError.value = value;
      }
    });

    function clearErrors() {
      props.isEdit ? updateReset() : createReset();
      error.value = null;
    }

    function resetFormValidation(): void {
      if (formRef.value) {
        return formRef.value.resetValidation();
      }
    }

    function validateForm(): boolean {
      let valid = false;
      if (formRef.value) {
        valid = formRef.value.validate();
      }
      return valid;
    }

    async function save() {
      clearErrors();
      const valid = await validateForm();

      if (valid) {
        let paymentPlanScheduleInstance: IPaymentPlanSchedule | null = null;

        try {
          paymentPlanScheduleInstance = new PaymentPlanSchedule(
            paymentPlanScheduleData.value
          );
        } catch (e) {
          // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
          error.value = e as ISystemError;
        }

        if (paymentPlanScheduleInstance != null) {
          const saveAction = props.isEdit
            ? updatePaymentPlan
            : createPaymentPlan;

          let payload = paymentPlanScheduleInstance;
          if (props.isEdit) {
            payload = diffDeep(
              paymentPlanScheduleInstance,
              props.paymentPlanSchedule,
              true
            );
          }

          saveAction(payload, {
            onSuccess: () => {
              closeDialog();
            }
          });
        }
      }
    }

    function updateDateTypeValue() {
      clearErrors();
      if (
        paymentPlanScheduleData.value.date_type ===
        PaymentPlanScheduleDateTypeEnum.Manual
      ) {
        paymentPlanScheduleData.value.number_of_weeks = null;
      } else {
        paymentPlanScheduleData.value.due_date = null;
      }
      setReminderStartDate();
    }

    // if calculated due date is in the past, then the reminder should be null.
    // Otherwise, if the calculated due date is in the future, the reminder should be 3 weeks before the due date.
    // If the 3 weeks before the due date is in the past, then the reminder should be 3 days in the future.
    function setReminderStartDate() {
      clearErrors();
      let reminderStartDate = null;
      if (
        calculatedDueDate() &&
        moment(calculatedDueDate()).isAfter(moment().utc())
      ) {
        reminderStartDate = moment(calculatedDueDate())
          .utc()
          .subtract(3, 'weeks')
          .format('YYYY-MM-DD');
        if (moment(reminderStartDate).isBefore(moment().utc())) {
          reminderStartDate = moment()
            .utc()
            .add(3, 'days')
            .format('YYYY-MM-DD');
        }
      }

      paymentPlanScheduleData.value.reminder_start_date = reminderStartDate;
    }

    /**
     * Calculates the due date based on the payment plan schedule data.
     *
     * The due date is determined by the `date_type` and `number_of_weeks` properties
     * of the `paymentPlanScheduleData` object. If the `date_type` is `ExFactory`, the due date
     * is calculated by subtracting the number of weeks from the `ex_factory_date` of the current
     * payment plan. If the `date_type` is `SignOff`, the due date is calculated by adding the number
     * of weeks to the `sign_off_date` of the current payment plan.
     *
     * @returns {string|null} The calculated due date in 'YYYY-MM-DD' format, or null if no due date is available.
     */
    function calculatedDueDate() {
      let dueDate = paymentPlanScheduleData.value.due_date ?? null;

      if (
        paymentPlanScheduleData.value.date_type ===
          PaymentPlanScheduleDateTypeEnum.ExFactory &&
        paymentPlanScheduleData.value.number_of_weeks
      ) {
        dueDate = moment(currentPaymentPlan.value.ex_factory_date)
          .utc()
          .subtract(paymentPlanScheduleData.value.number_of_weeks, 'weeks')
          .format('YYYY-MM-DD');
      } else if (
        paymentPlanScheduleData.value.date_type ===
          PaymentPlanScheduleDateTypeEnum.SignOff &&
        paymentPlanScheduleData.value.number_of_weeks
      ) {
        dueDate = moment(currentPaymentPlan.value.sign_off_date)
          .utc()
          .add(paymentPlanScheduleData.value.number_of_weeks, 'weeks')
          .format('YYYY-MM-DD');
      }
      return dueDate;
    }

    /**
     * Validates that the total amount of the payment schedules does not exceed the proposal amount.
     *
     * @param {string | null} value - The value to be validated.
     * @returns {boolean | string} - Returns true if the total amount of the schedules is less than or equal to the proposal amount.
     */
    function excessScheduleAmountValidator(
      value?: string | null
    ): boolean | string {
      let isExcess = !!value;
      if (value) {
        const proposalAmount = props.totalPlanAmount;

        let currentScheduleTotal = 0;
        if (
          paymentPlanScheduleData.value.amount_type ===
          PaymentPlanAmountTypeEnum.Percentage
        ) {
          currentScheduleTotal =
            (currentPaymentPlan.value.total_amount_scheduled ?? 0) +
            (proposalAmount * Number(value)) / 100;

          if (props.isEdit) {
            currentScheduleTotal -=
              (proposalAmount *
                Number(currentPaymentSchedule.value!.percentage)) /
              100;
          }
        } else {
          currentScheduleTotal =
            (currentPaymentPlan.value.total_amount_scheduled ?? 0) +
            Number(value);

          if (props.isEdit) {
            currentScheduleTotal -= Number(
              currentPaymentSchedule.value!.fixed_amount
            );
          }
        }

        isExcess =
          Number(currentScheduleTotal.toFixed(2)) >
          Number(proposalAmount.toFixed(2));
      }
      return isExcess;
    }

    return {
      PaymentPlanScheduleDateTypeEnum,
      PaymentPlanAmountTypeEnum,
      localDialog,
      paymentPlanScheduleData,
      amountTypeSwitch,
      valid,
      formRef,
      subtitle,
      loading,
      error,
      setReminderStartDate,
      excessScheduleAmountValidator,
      requiredValidator,
      updateDateTypeValue,
      closeDialog,
      save,
      clearErrors,
      updateAmountType,
      InvoiceStateEnum
    };
  }
});
